import React, { useEffect, useState, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import AuthContext from './AuthContext';
import { FirebaseAuth, FirestoreDB } from '../utils/firebase/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ACM } from '../constants/UserRoles';
import {
  registerWithGoogle,
  registerWithEmailPassword,
  registerWithEmailPasswordForTeacherAndStudent,
  registerNewPatient,
} from './authRegistration';
import {
  loginWithEmailPassword,
  loginWithGoogleProvider,
  getUserFromFirestore,
  getAdminUserFromFirestore,
  login,
  adminsLogin,
  logout,
} from './authLogin';
import {
  uploadProfilePic,
  updateGlobalUser,
  validateContactNo,
} from './authUtils';
import { movePreRegisteredUserToRealUser } from '../api/students';

// Global flag to prevent multiple AuthProvider instances
// eslint-disable-next-line no-unused-vars
let globalAuthProviderInstance = null;

const AuthProvider = ({ children }) => {
  // Check if another AuthProvider is already running

  globalAuthProviderInstance = 'active';

  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isAdminUserAuthenticted, setIsAdminUserAuthenticated] =
    useState(false);
  const [user, setUser] = useState(null);
  const [typeOfUser, setTypeOfUser] = useState(null);
  const [openRightPanel, setOpenRightPanel] = useState(false);
  const [openVideoUpload, setOpenVideoUpload] = useState(false);
  const [panelContent, setPanelContent] = useState();
  const [panelTitle, setPanelTitle] = useState('');
  const [isProfileCompleted, setIsProfileCompleted] = useState(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('isProfileCompleted');
    return saved === 'true';
  });
  const [isNotificationPanel, setIsNotificationPanel] = useState(false);
  const [isTreatmentsPanel, setIsTreatmentsPanel] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);

  // Add ref to track processed users and prevent duplicate processing
  const processedUsers = useRef(new Set());
  const isProcessing = useRef(false);
  const authTimeoutRef = useRef(null);
  const preRegisteredUserDataRef = useRef(null);
  const listenerSetupRef = useRef(false);
  const renderCountRef = useRef(0);

  // Check if we're in development mode
  // const isDevelopment = process.env.NODE_ENV === 'development';

  // Track component instances to debug multiple mounts
  // const instanceId = useRef(Math.random().toString(36).substr(2, 9));

  // Track route changes to see if they cause remounts
  const currentPath = useRef(window.location.pathname);

  // Increment render count on each render
  renderCountRef.current += 1;

  useEffect(() => {
    const handleRouteChange = () => {
      const newPath = window.location.pathname;
      if (newPath !== currentPath.current) {
        currentPath.current = newPath;
      }
    };

    // Listen for route changes
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  // const typeOfUsers = {
  //   TEACHER: 'teachersList',
  //   STUDENT: 'studentsList',
  //   SCHOOLADMIN: 'schoolAdminsList',
  //   ORGANIZATIONADMIN: 'organizationAdminsList',
  // };

  // Wrapped versions to pass state setters/variables as needed
  const wrappedLogin = (firebaseUser) => {
    // Preserve preRegisteredUserData from current user state, ref, or localStorage
    const currentUserPreRegisteredData =
      user?.preRegisteredUserData || preRegisteredUserDataRef.current;
    let localStoragePreRegisteredData = null;
    try {
      const stored = localStorage.getItem('tempPreRegisteredUserData');
      if (stored) {
        localStoragePreRegisteredData = JSON.parse(stored);
      }
    } catch (e) {
      console.log(' Error parsing localStorage preRegisteredUserData:', e);
    }

    const finalPreRegisteredData =
      currentUserPreRegisteredData || localStoragePreRegisteredData;
    const userWithPreRegisteredData = {
      ...firebaseUser,
      preRegisteredUserData: finalPreRegisteredData || null,
    };

    setIsLoginInProgress(true);
    return login(
      userWithPreRegisteredData,
      setIsUserAuthenticated,
      setUser,
      setTypeOfUser
    ).finally(() => {
      setIsLoginInProgress(false);
    });
  };

  const wrappedAdminsLogin = (user) =>
    adminsLogin(
      user,
      setIsAdminUserAuthenticated,
      setUser,
      wrappedGetAdminUserFromFirestore,
      setTypeOfUser
    );

  const wrappedGetUserFromFirestore = (uid, setTypeOfUserArg) =>
    getUserFromFirestore(uid, setTypeOfUserArg || setTypeOfUser);

  const wrappedGetAdminUserFromFirestore = (uid, setTypeOfUserArg) =>
    getAdminUserFromFirestore(uid, setTypeOfUserArg || setTypeOfUser);

  const wrappedLogout = () => {
    // Clear profile completion state on logout
    localStorage.removeItem('isProfileCompleted');
    processedUsers.current.clear();
    isProcessing.current = false;
    logout(
      FirebaseAuth,
      ACM,
      typeOfUser,
      setIsAdminUserAuthenticated,
      setIsUserAuthenticated,
      setUser,
      setTypeOfUser
    );
  };

  const wrappedLoginWithEmailPassword = (email, password) =>
    loginWithEmailPassword(
      email,
      password,
      setIsUserAuthenticated,
      setUser,
      setTypeOfUser,
      setIsAdminUserAuthenticated,
      wrappedAdminsLogin,
      wrappedLogin
    );

  const wrappedRegisterWithGoogle = (data, infoParams) =>
    registerWithGoogle(data, infoParams, wrappedLogin);

  const wrappedRegisterWithEmailPasswordForTeacherAndStudent = (data) =>
    registerWithEmailPasswordForTeacherAndStudent(data, uploadProfilePic);

  const wrappedRegisterNewPatient = (patientInfo, doctorId) =>
    registerNewPatient(patientInfo, doctorId, wrappedLogin, user);

  useEffect(() => {
    if (!openRightPanel) {
      setIsTreatmentsPanel(false);
    }
  }, [openRightPanel]);

  const wrappedUpdateGlobalUser = (data) => {
    updateGlobalUser(setUser, data);
  };
  useEffect(() => {
    if (!user) return;
    const savedRole = localStorage.getItem('selectedRole');
    const userRole = localStorage.getItem('userRole');

    // Check if user has roles array and if saved role is in it
    if (
      savedRole &&
      Array.isArray(user.roles) &&
      user.roles.includes(savedRole)
    ) {
      setTypeOfUser(savedRole);
    } else if (userRole === 'admin' && user?.userType === 'ADMIN') {
      // Special handling for admin users
      setTypeOfUser('ADMIN');
      localStorage.setItem('selectedRole', 'ADMIN');
    } else {
      const defaultRole = user?.roles?.[0] || user?.userType;
      setTypeOfUser(defaultRole);
      localStorage.setItem('selectedRole', defaultRole);
    }
  }, [user]);

  useEffect(() => {
    if (user?.uid) {
      if (ACM[typeOfUser]?.isPatient || ACM[typeOfUser]?.isStudent) {
        // Check if user is in studentsList (migrated user) - they should be considered profile complete
        const isMigratedStudent =
          user?.isMigrated ||
          user?.migrationStatus === 'completed' ||
          user?.migrationStatus === 'in_progress';
        const isPreRegisteredUser = user?.preRegisteredUserData?.id;
        const vaultProfile = user?.['0'];
        const profileData = vaultProfile || user;

        if (isMigratedStudent) {
          // For migrated students, check if they have complete profile data
          const contactNoValid = validateContactNo(profileData?.contactNo);
          const hasFullname = profileData?.fullname;
          const hasEmail = profileData?.email;

          // Calculate age from DOB if age is missing
          let calculatedAge = profileData?.age;
          if (
            (!calculatedAge ||
              calculatedAge === undefined ||
              calculatedAge === null) &&
            profileData?.dob
          ) {
            const today = new Date();
            const birthDate = new Date(profileData.dob);
            calculatedAge = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (
              monthDiff < 0 ||
              (monthDiff === 0 && today.getDate() < birthDate.getDate())
            ) {
              calculatedAge--;
            }
          }

          // Fix age validation - handle undefined/null values properly
          const hasValidAge =
            calculatedAge !== undefined &&
            calculatedAge !== null &&
            calculatedAge !== '' &&
            calculatedAge >= 0;
          const hasGender = profileData?.gender !== '';
          const hasDob = profileData?.dob !== '';

          if (
            hasFullname &&
            hasEmail &&
            hasValidAge &&
            hasGender &&
            hasDob &&
            contactNoValid
          ) {
            setIsProfileCompleted(true);
            localStorage.setItem('isProfileCompleted', 'true');
          } else {
            // For migrated students, if they don't have complete profile, show the form
            setIsProfileCompleted(false);
          }
        } else if (isPreRegisteredUser && !isMigratedStudent) {
          setIsProfileCompleted(false);
        } else {
          const currentVaultProfile = user?.['0'];
          let currentProfileData = currentVaultProfile || user;

          // Calculate age from DOB if age is missing
          if (
            currentProfileData &&
            !currentProfileData.age &&
            currentProfileData.dob
          ) {
            const dob = new Date(currentProfileData.dob);
            const today = new Date();
            const age = Math.floor(
              (today - dob) / (365.25 * 24 * 60 * 60 * 1000)
            );
            currentProfileData = {
              ...currentProfileData,
              age: age,
            };
          }

          const contactNoValid = validateContactNo(
            currentProfileData?.contactNo
          );

          const hasValidAge =
            currentProfileData?.age >= 0 && currentProfileData?.age !== '';
          const hasGender = currentProfileData?.gender !== '';
          const hasDob = currentProfileData?.dob !== '';

          if (hasValidAge && hasGender && hasDob && contactNoValid) {
            setIsProfileCompleted(true);
            // Auto-detect and persist profile completion for existing users
            localStorage.setItem('isProfileCompleted', 'true');
          } else {
            setIsProfileCompleted(false);
          }
        }
      } else if (ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isTeacher) {
        setIsProfileCompleted(true);
      }
    }
  }, [user, typeOfUser]);

  useEffect(() => {
    // Prevent multiple listener setups
    if (listenerSetupRef.current) {
      return;
    }

    // Check if FirebaseAuth is available
    if (!FirebaseAuth) {
      console.error('FirebaseAuth is not available');
      return;
    }

    // Check if this is the same Firebase auth instance
    if (window.FirebaseAuth && window.FirebaseAuth !== FirebaseAuth) {
      console.warn(
        'Different Firebase auth instances detected! This may cause authentication issues.'
      );
      console.warn('Window FirebaseAuth:', window.FirebaseAuth);
      console.warn('Imported FirebaseAuth:', FirebaseAuth);
    }

    listenerSetupRef.current = true;
    let isMounted = true;

    const handleInvitationPageLogout = async () => {
      const isInvitationPage = window.location.pathname.includes('/invite/');
      const urlParams = new URLSearchParams(window.location.search);
      const isEmailVerification = urlParams.get('mode') === 'verifyEmail';
      if (
        isInvitationPage &&
        window.FirebaseAuth?.currentUser &&
        !isEmailVerification
      ) {
        try {
          await window.FirebaseAuth.signOut();
          localStorage.removeItem('session');
          localStorage.removeItem('userRole');
          if (isMounted) {
            setIsUserAuthenticated(false);
            setIsAdminUserAuthenticated(false);
            setUser(null);
          }
        } catch {}
      }
    };
    handleInvitationPageLogout();

    const unsubscribe = onAuthStateChanged(FirebaseAuth, async (user) => {
      if (!isMounted) return;

      // Clear any existing timeout
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }

      // Debounce the authentication processing
      authTimeoutRef.current = setTimeout(async () => {
        if (!isMounted) return;

        const isInvitationPage = window.location.pathname.includes('/invite/');
        const urlParams = new URLSearchParams(window.location.search);
        const isEmailVerification = urlParams.get('mode') === 'verifyEmail';

        try {
          if (user) {
            // Prevent duplicate processing of the same user
            if (processedUsers.current.has(user.uid) && !isProcessing.current) {
              return;
            }

            if (isProcessing.current) {
              return;
            }

            isProcessing.current = true;
            processedUsers.current.add(user.uid);

            if (isInvitationPage) {
              if (isEmailVerification) {
                if (user.emailVerified) {
                  // Check if this is a pre-registered student that needs migration
                  let preRegisteredUserData = null;

                  // Try to get preRegisteredUserData from multiple sources
                  if (user.preRegisteredUserData) {
                    preRegisteredUserData = user.preRegisteredUserData;
                  } else if (preRegisteredUserDataRef.current) {
                    preRegisteredUserData = preRegisteredUserDataRef.current;
                  } else {
                    try {
                      const stored = localStorage.getItem(
                        'tempPreRegisteredUserData'
                      );
                      if (stored) {
                        preRegisteredUserData = JSON.parse(stored);
                      }
                    } catch (e) {
                      console.error(
                        'Error parsing preRegisteredUserData from localStorage:',
                        e
                      );
                    }
                  }

                  // If pre-registered user data exists, trigger migration
                  if (
                    preRegisteredUserData &&
                    preRegisteredUserData.id &&
                    user.uid
                  ) {
                    try {
                      const migrationResult =
                        await movePreRegisteredUserToRealUser(
                          preRegisteredUserData.id,
                          user.uid
                        );

                      if (migrationResult) {
                        // Clear stored preRegisteredUserData after successful migration
                        if (preRegisteredUserDataRef.current) {
                          preRegisteredUserDataRef.current = null;
                        }
                        localStorage.removeItem('tempPreRegisteredUserData');

                        // Clear from user object
                        delete user.preRegisteredUserData;
                      } else {
                        console.error('Migration returned null/undefined');
                      }
                    } catch (migrationError) {
                      console.error(
                        ' Failed to migrate preRegistered user:',
                        migrationError
                      );
                      // Continue with login even if migration fails - user can retry later
                    }
                  }

                  const storedRole = localStorage.getItem('userRole');
                  if (storedRole === 'admin') {
                    if (isMounted) {
                      await wrappedAdminsLogin(user);
                      setIsAdminUserAuthenticated(true);
                    }
                  } else {
                    if (isMounted) {
                      await wrappedLogin(user);
                      setIsUserAuthenticated(true);
                    }
                  }
                }
                isProcessing.current = false;
                return;
              }
              try {
                await signOut(FirebaseAuth);
                localStorage.removeItem('session');
                localStorage.removeItem('userRole');
                if (isMounted) {
                  setIsUserAuthenticated(false);
                  setIsAdminUserAuthenticated(false);
                  setUser(null);
                }
              } catch (logoutError) {
                console.error('Error logging out user:', logoutError);
              }
              isProcessing.current = false;
              return;
            }

            const storedRole = localStorage.getItem('userRole');

            if (storedRole === 'admin') {
              if (isMounted) {
                await wrappedAdminsLogin(user);
                setIsAdminUserAuthenticated(true);
              }
            } else if (storedRole === 'user') {
              if (isMounted && !isUserAuthenticated && !isLoginInProgress) {
                await wrappedLogin(user);
                setIsUserAuthenticated(true);
              } else {
                console.log(
                  ' onAuthStateChanged: Skipping wrappedLogin - user already authenticated or login in progress'
                );
              }
            } else {
              // Only check admin status if we don't have a stored role
              // and we haven't checked this user before
              const userRoleKey = `userRole_${user.uid}`;
              const cachedRole = localStorage.getItem(userRoleKey);

              if (cachedRole) {
                // Use cached role to avoid database call
                if (cachedRole === 'admin') {
                  localStorage.setItem('userRole', 'admin');
                  if (isMounted) {
                    await wrappedAdminsLogin(user);
                    setIsAdminUserAuthenticated(true);
                  }
                } else {
                  localStorage.setItem('userRole', 'user');
                  if (isMounted && !isUserAuthenticated && !isLoginInProgress) {
                    await wrappedLogin(user);
                    setIsUserAuthenticated(true);
                  }
                }
              } else {
                // Only make database call once per user
                const adminDocRef = await getDoc(
                  doc(FirestoreDB, 'adminsList', user.uid)
                );

                if (!isMounted) return;

                if (adminDocRef.exists()) {
                  localStorage.setItem('userRole', 'admin');
                  localStorage.setItem(userRoleKey, 'admin');
                  if (isMounted) {
                    await wrappedAdminsLogin(user);
                    setIsAdminUserAuthenticated(true);
                  }
                } else {
                  localStorage.setItem('userRole', 'user');
                  localStorage.setItem(userRoleKey, 'user');
                  if (isMounted && !isUserAuthenticated && !isLoginInProgress) {
                    await wrappedLogin(user);
                    setIsUserAuthenticated(true);
                  }
                }
              }
            }

            isProcessing.current = false;
          } else {
            if (isInvitationPage) {
              return;
            }
            if (isMounted) {
              setIsUserAuthenticated(false);
              setIsAdminUserAuthenticated(false);
              setUser(null);
              localStorage.removeItem('session');
              localStorage.removeItem('userRole');
            }
          }
        } catch (error) {
          isProcessing.current = false;
          if (isInvitationPage) {
            return;
          }
          if (isMounted) {
            setIsUserAuthenticated(false);
            setIsAdminUserAuthenticated(false);
            setUser(null);
            localStorage.removeItem('userRole');
          }
        }
      }, 100); // 100ms debounce
    });

    return () => {
      globalAuthProviderInstance = null;
      listenerSetupRef.current = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      processedUsers.current.clear();
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }
      isMounted = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isUserAuthenticated,
        isAdminUserAuthenticted,
        user,
        setUser,
        registerWithGoogle: wrappedRegisterWithGoogle,
        registerWithEmailPassword,
        loginWithEmailPassword: wrappedLoginWithEmailPassword,
        loginWithGoogleProvider,
        login: wrappedLogin,
        logout: wrappedLogout,
        typeOfUser,
        setTypeOfUser,
        selectedUserData,
        setSelectedUserData,
        openRightPanel,
        setOpenRightPanel,
        openVideoUpload,
        setOpenVideoUpload,
        panelContent,
        setPanelContent,
        panelTitle,
        setPanelTitle,
        isProfileCompleted,
        setIsProfileCompleted,
        isNotificationPanel,
        setIsNotificationPanel,
        isTreatmentsPanel,
        setIsTreatmentsPanel,
        registerNewPatient: wrappedRegisterNewPatient,
        updateGlobalUser: wrappedUpdateGlobalUser,
        getUserFromFirestore: wrappedGetUserFromFirestore,
        getAdminUserFromFirestore: wrappedGetAdminUserFromFirestore,
        registerWithEmailPasswordForTeacherAndStudent:
          wrappedRegisterWithEmailPasswordForTeacherAndStudent,
        uploadProfilePic,
        preRegisteredUserDataRef,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
